import { useCallback, useEffect, useMemo, useState } from "react"
import axios from "axios"
import { toast } from "sonner"
import {
  approvePermitStage,
  getPermitWorkflowTimeline,
  rejectPermitStage,
  requestReviewPermitStage,
  type WorkflowActionResponse,
  type PermitWorkflowStageTimeline,
} from "modules/permit"
import type { PermitWorkflowTimeline } from "modules/permit"
import type { PermitPageMode, PermitRequestLayoutState } from "../types"
import {
  isClosedStatus,
  isOpenStatus,
  isRequesterInformationStatus,
  isRevisionStatus,
} from "modules/permit/constants/permitStatus"
import { requestNavCountsRefresh } from "modules/permit/utils/navCountRefresh"

const isGuidValue = (value: string | null | undefined): boolean => {
  if (!value) return false
  return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(value)
}

const normaliseCandidate = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null
  }
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

const extractWarningMessages = (warnings: unknown): string[] => {
  if (!Array.isArray(warnings)) {
    return []
  }

  return warnings
    .map((warning) => {
      if (typeof warning === "string") {
        return warning.trim()
      }
      if (warning && typeof warning === "object" && "message" in warning) {
        const message = (warning as { message?: unknown }).message
        return typeof message === "string" ? message.trim() : ""
      }
      return ""
    })
    .filter(Boolean)
}

const APPROVER_NOT_FOUND_PATTERN =
  /(approver|approval|ผู้อนุมัติ|อนุมัติ).*(not found|missing|ไม่พบ|ไม่มี|หา.*ไม่เจอ)|(not found|missing|ไม่พบ|ไม่มี|หา.*ไม่เจอ).*(approver|approval|ผู้อนุมัติ|อนุมัติ)/i

const MISSING_APPROVER_ERROR_MESSAGE = "\u0E44\u0E21\u0E48\u0E21\u0E35\u0E02\u0E49\u0E2D\u0E21\u0E39\u0E25\u0E1C\u0E39\u0E49\u0E2D\u0E19\u0E38\u0E21\u0E31\u0E15\u0E34"

const extractWorkflowActionError = (response?: WorkflowActionResponse | null): string | null => {
  if (!response) {
    return null
  }

  const candidateMessages = [
    response.errorMessage,
    response.message,
    response.warningMessage,
    ...extractWarningMessages(response.warnings),
  ]
    .map((value) => (typeof value === "string" ? value.trim() : ""))
    .filter(Boolean)

  if (candidateMessages.some((message) => APPROVER_NOT_FOUND_PATTERN.test(message))) {
    return MISSING_APPROVER_ERROR_MESSAGE
  }

  if (response.success === false || response.nextApproverFound === false) {
    return candidateMessages[0] ?? MISSING_APPROVER_ERROR_MESSAGE
  }

  if (false) {
    return (
      candidateMessages[0] ??
      "ไม่สามารถหา approver คนถัดไปใน Workflow ได้"
    )
  }

  const approverError = candidateMessages.find((message) => APPROVER_NOT_FOUND_PATTERN.test(message))
  return approverError ? MISSING_APPROVER_ERROR_MESSAGE : null
}

const getNormalizedUserId = (user?: Record<string, any> | null): string | null => {
  if (!user) {
    return null
  }
  const candidates: Array<string | null> = [
    normaliseCandidate(user.id),
    normaliseCandidate(user.user_id),
    normaliseCandidate(user.userId),
    normaliseCandidate(user.emp_id),
    normaliseCandidate(user.empId),
    normaliseCandidate(user.employee_id),
    normaliseCandidate(user.employeeId),
  ]

  let fallback: string | null = null

  for (const value of candidates) {
    if (!value) continue
    const normalized = value.toLowerCase()
    if (isGuidValue(value)) {
      return normalized
    }
    if (!fallback) {
      fallback = normalized
    }
  }

  return fallback
}

interface UseWorkflowApprovalActionsParams {
  tenantId: string
  mode: PermitPageMode
  permitStatus: string
  activeWorkflowStage: PermitWorkflowStageTimeline | null
  editingPermitId: string | null
  currentUser: PermitRequestLayoutState["user"]
  refreshPermitDetail: () => void
  workflowTimeline: PermitWorkflowTimeline | null
  // At P0251 (REQUESTER_INFORMATION), the requester must always be able to respond on the
  // REQUESTER_REVISION stage even if that stage's assignedUserId/assignedRoleId doesn't
  // literally match the current user (e.g. it wasn't assigned directly to the requester).
  canActAsRequesterForInfoRequest?: boolean
}

interface WorkflowRejectionNote {
  stageName?: string | null
  note?: string | null
  actedBy?: string | null
  actedAt?: string | null
}

interface UseWorkflowApprovalActionsResult {
  currentStageDisplayName: string
  canApproveCurrentStage: boolean
  canRequestReviewCurrentStage: boolean
  approvalNote: string
  setApprovalNote: (value: string) => void
  rejectNote: string
  setRejectNote: (value: string) => void
  rejectNoteError: string | null
  requestReviewNote: string
  setRequestReviewNote: (value: string) => void
  requestReviewNoteError: string | null
  approvalSuccessMessage: string | null
  approvalActionError: string | null
  isApproveDialogOpen: boolean
  setIsApproveDialogOpen: (open: boolean) => void
  isRejectDialogOpen: boolean
  setIsRejectDialogOpen: (open: boolean) => void
  isRequestReviewDialogOpen: boolean
  setIsRequestReviewDialogOpen: (open: boolean) => void
  isApprovingStage: boolean
  isRejectingStage: boolean
  isRequestReviewingStage: boolean
  handleOpenApproveDialog: () => void
  handleOpenRejectDialog: () => void
  handleOpenRequestReviewDialog: () => void
  handleConfirmApproveStage: () => Promise<boolean>
  handleConfirmRejectStage: () => Promise<boolean>
  handleConfirmRequestReviewStage: () => Promise<boolean>
  latestRevisionNote: WorkflowRejectionNote | null
  latestRequestReviewNote: WorkflowRejectionNote | null
}

export const useWorkflowApprovalActions = ({
  tenantId,
  mode,
  permitStatus,
  activeWorkflowStage,
  editingPermitId,
  currentUser,
  refreshPermitDetail,
  workflowTimeline,
  canActAsRequesterForInfoRequest = false,
}: UseWorkflowApprovalActionsParams): UseWorkflowApprovalActionsResult => {
  const [approvalSuccessMessage, setApprovalSuccessMessage] = useState<string | null>(null)
  const [approvalActionError, setApprovalActionError] = useState<string | null>(null)
  const [isApproveDialogOpen, setIsApproveDialogOpen] = useState(false)
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false)
  const [isRequestReviewDialogOpen, setIsRequestReviewDialogOpen] = useState(false)
  const [approvalNote, setApprovalNote] = useState("")
  const [rejectNote, setRejectNote] = useState("")
  const [rejectNoteError, setRejectNoteError] = useState<string | null>(null)
  const [requestReviewNote, setRequestReviewNote] = useState("")
  const [requestReviewNoteError, setRequestReviewNoteError] = useState<string | null>(null)
  const [isApprovingStage, setIsApprovingStage] = useState(false)
  const [isRejectingStage, setIsRejectingStage] = useState(false)
  const [isRequestReviewingStage, setIsRequestReviewingStage] = useState(false)

  const currentUserId = useMemo(() => getNormalizedUserId(currentUser), [currentUser])
  // Use roleId-based matching to reduce ambiguity when stages are assigned by role
  const currentUserRoleIds = useMemo(() => {
    const normalized = new Set<string>()
    const add = (value?: string | null) => {
      if (typeof value !== "string") return
      const trimmed = value.trim()
      if (!trimmed) return
      normalized.add(trimmed.toLowerCase())
    }

    if (currentUser) {
      add((currentUser as any).roleId)
      add((currentUser as any).role_id)
      add(currentUser.role?.id)

      const userRoles =
        (Array.isArray((currentUser as any).roles) && (currentUser as any).roles) ||
        (Array.isArray((currentUser as any).user_roles) && (currentUser as any).user_roles) ||
        []

      for (const role of userRoles as Array<any>) {
        add(role?.roleId ?? role?.role_id ?? role?.id)
      }
    }
    return normalized
  }, [currentUser])

  // Same extraction pattern used for contractorCompanyId elsewhere (e.g. PermitIndexPage.tsx).
  const currentUserCompanyId = useMemo(() => {
    if (!currentUser) return null
    const candidates: Array<unknown> = [
      (currentUser as any).companyId,
      (currentUser as any).company_id,
      (currentUser as any).company?.companyId,
      (currentUser as any).company?.id,
    ]
    for (const candidate of candidates) {
      if (typeof candidate === "string" && candidate.trim()) {
        return candidate.trim().toLowerCase()
      }
    }
    return null
  }, [currentUser])

  const currentStageDisplayName =
    activeWorkflowStage?.name || activeWorkflowStage?.code || "ขั้นตอนนี้"

  const canApproveCurrentStage = useMemo(() => {
    if (mode !== "edit" || !activeWorkflowStage) {
      return false
    }

    const stageStatus = (activeWorkflowStage.status ?? "").toUpperCase()
    if (stageStatus !== "IN_PROGRESS") {
      return false
    }

    if (isRevisionStatus(permitStatus) || isClosedStatus(permitStatus) || isOpenStatus(permitStatus)) {
      return false
    }

    const stageCode = (activeWorkflowStage.code ?? "").trim().toUpperCase()
    // On the requester-info-request stage, the requester (or a same-company colleague with
    // requester-side permissions) can always respond, even if this stage instance wasn't
    // assigned directly to them.
    const isRequesterInfoBypass =
      isRequesterInformationStatus(permitStatus) &&
      stageCode === "REQUESTER_REVISION" &&
      canActAsRequesterForInfoRequest

    const assignedUserNormalized =
      typeof activeWorkflowStage.assignedUserId === "string"
        ? activeWorkflowStage.assignedUserId.trim().toLowerCase()
        : null

    if (assignedUserNormalized && !isRequesterInfoBypass) {
      if (!currentUserId) {
        return false
      }
      if (assignedUserNormalized !== currentUserId) {
        // Fall back to "same contractor company as the assigned approver" so a colleague can
        // stand in (e.g. covering while the assigned approver is out) - the backend doesn't
        // enforce an exact user-id match server-side, it just records whoever actually acts.
        const assignedCompanyNormalized =
          typeof activeWorkflowStage.assignedUserCompanyId === "string"
            ? activeWorkflowStage.assignedUserCompanyId.trim().toLowerCase()
            : null
        const sameCompany =
          Boolean(assignedCompanyNormalized) &&
          Boolean(currentUserCompanyId) &&
          assignedCompanyNormalized === currentUserCompanyId

        if (!sameCompany) {
          return false
        }
      }
    }

    const isControllerStage =
      (activeWorkflowStage.code || activeWorkflowStage.name || "")
        .toString()
        .trim()
        .toUpperCase() === "CONTROLLER"

    // Non-controller stages rely on roleId match only
    if (!isControllerStage && !isRequesterInfoBypass) {
      const assignedRoleId =
        typeof activeWorkflowStage.assignedRoleId === "string"
          ? activeWorkflowStage.assignedRoleId.trim().toLowerCase()
          : null

      if (assignedRoleId) {
        if (currentUserRoleIds.size === 0) {
          return false
        }
        if (!currentUserRoleIds.has(assignedRoleId)) {
          return false
        }
      }
    }

    return true
  }, [
    activeWorkflowStage,
    canActAsRequesterForInfoRequest,
    currentUserId,
    currentUserCompanyId,
    currentUserRoleIds,
    mode,
    permitStatus,
  ])

  const canRequestReviewCurrentStage = useMemo(() => {
    if (!canApproveCurrentStage || !activeWorkflowStage) {
      return false
    }

    const stageCode = (activeWorkflowStage.code ?? "").trim().toUpperCase()
    return stageCode !== "REQUESTER_REVISION"
  }, [activeWorkflowStage, canApproveCurrentStage])

  const verifyPostApprovalState = useCallback(async (): Promise<string | null> => {
    if (!editingPermitId || !activeWorkflowStage) {
      return null
    }

    try {
      const timeline = await getPermitWorkflowTimeline(tenantId, editingPermitId).catch(() => null)

      const remainingStages =
        timeline?.stages?.filter((stage) => stage.seqIndex > activeWorkflowStage.seqIndex) ?? []

      const missingApproverStage =
        remainingStages.find((stage) => {
          const stageStatus = (stage.status ?? "").trim().toUpperCase()
          const missingApproverBehavior = (stage.onMissingApprover ?? "FAIL").trim().toUpperCase()
          const hasAssignment = Boolean(
            normaliseCandidate(stage.assignedUserId) ??
              normaliseCandidate(stage.assignedRoleId) ??
              normaliseCandidate(stage.assignedRoleCode) ??
              normaliseCandidate(stage.assignedRoleName),
          )

          return stageStatus === "PENDING" && missingApproverBehavior === "FAIL" && !hasAssignment
        }) ?? null

      if (missingApproverStage) {
        return MISSING_APPROVER_ERROR_MESSAGE
      }

      if (missingApproverStage && false) {
        return `ไม่พบผู้อนุมัติขั้นตอนถัดไป (${missingApproverStage!.name ?? missingApproverStage!.code ?? `Stage ${missingApproverStage!.seqIndex}`})`
      }

      if (false) {
        return "ไม่พบผู้อนุมัติขั้นตอนถัดไป ระบบไม่สามารถเดิน Workflow ต่อได้"
      }

      return null
    } catch (error) {
      console.error("Failed to verify post-approval workflow state", error)
      return null
    }
  }, [activeWorkflowStage, editingPermitId, tenantId])

  const handleConfirmApproveStage = useCallback(async (): Promise<boolean> => {
    if (!canApproveCurrentStage || !activeWorkflowStage || !editingPermitId) {
      return false
    }
    if (!currentUserId) {
      setApprovalActionError("ไม่พบข้อมูลผู้ใช้งานสำหรับบันทึกผู้ดำเนินการ")
      return false
    }

    setIsApprovingStage(true)
    setApprovalActionError(null)

    try {
      // Debug payload before calling approve API to inspect what we send from the UI.
      console.log("[permit] approving stage payload", {
        tenantId,
        permitId: editingPermitId,
        stageId: activeWorkflowStage.stageId,
        actionNote: approvalNote.trim().length > 0 ? approvalNote.trim() : undefined,
        stage: activeWorkflowStage,
      })

      const response = await approvePermitStage({
        tenantId,
        permitId: editingPermitId,
        stageId: activeWorkflowStage.stageId,
        actedByUserId: currentUserId,
        actionNote: approvalNote.trim().length > 0 ? approvalNote.trim() : undefined,
      })
      const responseError = extractWorkflowActionError(response)
      if (responseError) {
        setApprovalSuccessMessage(null)
        setApprovalActionError(responseError)
        toast.error(responseError)
        refreshPermitDetail()
        return false
      }

      const verificationError = await verifyPostApprovalState()
      if (verificationError) {
        setApprovalSuccessMessage(null)
        setApprovalActionError(verificationError)
        toast.error(verificationError)
        refreshPermitDetail()
        return false
      }
      if (response.emailErrorMessage) {
        toast.error(`อนุมัติสำเร็จ แต่ส่งอีเมลไม่สำเร็จ: ${response.emailErrorMessage}`)
      }
      setApprovalSuccessMessage("อนุมัติขั้นตอนเรียบร้อยแล้ว")
      setIsApproveDialogOpen(false)
      setApprovalNote("")
      requestNavCountsRefresh()
      refreshPermitDetail()
      return true
    } catch (error) {
      console.error("Failed to approve permit stage", error)
      const message = axios.isAxiosError(error)
        ? (error.response?.data as any)?.message ??
          error.response?.statusText ??
          error.message ??
          "ไม่สามารถอนุมัติขั้นตอนได้"
        : error instanceof Error
          ? error.message
          : "ไม่สามารถอนุมัติขั้นตอนได้"
      setApprovalActionError(message)
      toast.error(message)
      return false
    } finally {
      setIsApprovingStage(false)
    }
  }, [
    activeWorkflowStage,
    approvalNote,
    canApproveCurrentStage,
    currentUserId,
    editingPermitId,
    refreshPermitDetail,
    tenantId,
    verifyPostApprovalState,
  ])

  const handleConfirmRejectStage = useCallback(async (): Promise<boolean> => {
    if (!canApproveCurrentStage || !activeWorkflowStage || !editingPermitId) {
      return false
    }
    if (rejectNote.trim().length === 0) {
      setRejectNoteError("กรุณาระบุเหตุผลก่อนส่งกลับ")
      return false
    }
    if (!currentUserId) {
      setApprovalActionError("ไม่พบข้อมูลผู้ใช้งานสำหรับบันทึกผู้ดำเนินการ")
      return false
    }

    setIsRejectingStage(true)
    setApprovalActionError(null)

    try {
      const response = await rejectPermitStage({
        tenantId,
        permitId: editingPermitId,
        stageId: activeWorkflowStage.stageId,
        actedByUserId: currentUserId,
        actionNote: rejectNote.trim(),
      })
      if (response.emailErrorMessage) {
        toast.error(`ส่งกลับสำเร็จ แต่ส่งอีเมลไม่สำเร็จ: ${response.emailErrorMessage}`)
      }
      setApprovalSuccessMessage("ส่งกลับผู้ขอเรียบร้อยแล้ว")
      setIsRejectDialogOpen(false)
      setRejectNote("")
      requestNavCountsRefresh()
      refreshPermitDetail()
      return true
    } catch (error) {
      console.error("Failed to reject permit stage", error)
      const message = axios.isAxiosError(error)
        ? (error.response?.data as any)?.message ??
          error.response?.statusText ??
          error.message ??
          "ไม่สามารถส่งกลับขั้นตอนได้"
        : error instanceof Error
          ? error.message
          : "ไม่สามารถส่งกลับขั้นตอนได้"
      setApprovalActionError(message)
      toast.error(message)
      return false
    } finally {
      setIsRejectingStage(false)
    }
  }, [
    activeWorkflowStage,
    canApproveCurrentStage,
    currentUserId,
    editingPermitId,
    refreshPermitDetail,
    rejectNote,
    tenantId,
  ])

  const handleConfirmRequestReviewStage = useCallback(async (): Promise<boolean> => {
    if (!canRequestReviewCurrentStage || !activeWorkflowStage || !editingPermitId) {
      return false
    }
    if (requestReviewNote.trim().length === 0) {
      setRequestReviewNoteError("กรุณาระบุเหตุผลก่อนขอข้อมูลเพิ่มเติม")
      return false
    }
    if (!currentUserId) {
      setApprovalActionError("ไม่พบข้อมูลผู้ใช้งานสำหรับบันทึกผู้ดำเนินการ")
      return false
    }

    setIsRequestReviewingStage(true)
    setApprovalActionError(null)

    try {
      const response = await requestReviewPermitStage({
        tenantId,
        permitId: editingPermitId,
        stageId: activeWorkflowStage.stageId,
        actedByUserId: currentUserId,
        actionNote: requestReviewNote.trim(),
      })
      if (response.emailErrorMessage) {
        toast.error(`ขอข้อมูลเพิ่มเติมสำเร็จ แต่ส่งอีเมลไม่สำเร็จ: ${response.emailErrorMessage}`)
      }
      setApprovalSuccessMessage("ส่งคำขอข้อมูลเพิ่มเติมเรียบร้อยแล้ว")
      setIsRequestReviewDialogOpen(false)
      setRequestReviewNote("")
      requestNavCountsRefresh()
      refreshPermitDetail()
      return true
    } catch (error) {
      console.error("Failed to request additional permit information", error)
      const message = axios.isAxiosError(error)
        ? (error.response?.data as any)?.message ??
          error.response?.statusText ??
          error.message ??
          "ไม่สามารถขอข้อมูลเพิ่มเติมได้"
        : error instanceof Error
          ? error.message
          : "ไม่สามารถขอข้อมูลเพิ่มเติมได้"
      setApprovalActionError(message)
      toast.error(message)
      return false
    } finally {
      setIsRequestReviewingStage(false)
    }
  }, [
    activeWorkflowStage,
    canRequestReviewCurrentStage,
    currentUserId,
    editingPermitId,
    refreshPermitDetail,
    requestReviewNote,
    tenantId,
  ])

  const handleOpenApproveDialog = useCallback(() => {
    if (!canApproveCurrentStage || isApprovingStage) {
      return
    }
    setApprovalActionError(null)
    setIsApproveDialogOpen(true)
  }, [canApproveCurrentStage, isApprovingStage])

  const handleOpenRejectDialog = useCallback(() => {
    if (!canApproveCurrentStage || isRejectingStage) {
      return
    }
    setApprovalActionError(null)
    setRejectNoteError(null)
    setIsRejectDialogOpen(true)
  }, [canApproveCurrentStage, isRejectingStage])

  const handleOpenRequestReviewDialog = useCallback(() => {
    if (!canRequestReviewCurrentStage || isRequestReviewingStage) {
      return
    }
    setApprovalActionError(null)
    setRequestReviewNoteError(null)
    setIsRequestReviewDialogOpen(true)
  }, [canRequestReviewCurrentStage, isRequestReviewingStage])

  useEffect(() => {
    setApprovalActionError(null)
    setApprovalSuccessMessage(null)
    setApprovalNote("")
    setRejectNote("")
    setRejectNoteError(null)
    setRequestReviewNote("")
    setRequestReviewNoteError(null)
    setIsApproveDialogOpen(false)
    setIsRejectDialogOpen(false)
    setIsRequestReviewDialogOpen(false)
  }, [editingPermitId])

  useEffect(() => {
    if (!canApproveCurrentStage) {
      setIsApproveDialogOpen(false)
      setIsRejectDialogOpen(false)
      setIsRequestReviewDialogOpen(false)
    }
  }, [canApproveCurrentStage])

  useEffect(() => {
    if (rejectNoteError && rejectNote.trim().length > 0) {
      setRejectNoteError(null)
    }
  }, [rejectNote, rejectNoteError])

  useEffect(() => {
    if (requestReviewNoteError && requestReviewNote.trim().length > 0) {
      setRequestReviewNoteError(null)
    }
  }, [requestReviewNote, requestReviewNoteError])

  const buildLatestWorkflowNote = useCallback(
    (targetStatuses: string[]): WorkflowRejectionNote | null => {
      if (!workflowTimeline?.history?.length) {
        return null
      }

      const targetStatusSet = new Set(targetStatuses.map((status) => status.toUpperCase()))
      const stageMap = new Map<string, PermitWorkflowStageTimeline>(
        (workflowTimeline.stages ?? []).map(
          (stage) => [stage.stageId, stage] as [string, PermitWorkflowStageTimeline],
        ),
      )

      const matchedEntries = workflowTimeline.history.filter((entry) => {
        const statusAfter = (entry.statusAfter ?? "").toUpperCase()
        return targetStatusSet.has(statusAfter)
      })

      let latest: WorkflowRejectionNote | null = null
      let latestTs = 0

      matchedEntries.forEach((entry) => {
        const currentTs = entry.actedAt ? Date.parse(entry.actedAt) : 0
        if (currentTs >= latestTs) {
          latestTs = currentTs
          const stage = stageMap.get(entry.stageId)
          latest = {
            stageName: stage?.name ?? stage?.code ?? null,
            note: entry.actionNote ?? null,
            actedBy: entry.actedByUserName ?? entry.actedByRoleName ?? null,
            actedAt: entry.actedAt,
          }
        }
      })

      return latest
    },
    [workflowTimeline],
  )

  const latestRevisionNote = useMemo<WorkflowRejectionNote | null>(() => {
    return buildLatestWorkflowNote(["REJECTED"])
  }, [buildLatestWorkflowNote])

  const latestRequestReviewNote = useMemo<WorkflowRejectionNote | null>(() => {
    return buildLatestWorkflowNote(["RETURNED"])
  }, [buildLatestWorkflowNote])

  return {
    currentStageDisplayName,
    canApproveCurrentStage,
    canRequestReviewCurrentStage,
    approvalNote,
    setApprovalNote,
    rejectNote,
    setRejectNote,
    rejectNoteError,
    requestReviewNote,
    setRequestReviewNote,
    requestReviewNoteError,
    approvalSuccessMessage,
    approvalActionError,
    isApproveDialogOpen,
    setIsApproveDialogOpen,
    isRejectDialogOpen,
    setIsRejectDialogOpen,
    isRequestReviewDialogOpen,
    setIsRequestReviewDialogOpen,
    isApprovingStage,
    isRejectingStage,
    isRequestReviewingStage,
    handleOpenApproveDialog,
    handleOpenRejectDialog,
    handleOpenRequestReviewDialog,
    handleConfirmApproveStage,
    handleConfirmRejectStage,
    handleConfirmRequestReviewStage,
    latestRevisionNote,
    latestRequestReviewNote,
  }
}
